import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  AttachFileRounded,
  AutoAwesomeRounded,
  CheckCircleRounded,
  CloseRounded,
  CloudDoneRounded,
  DownloadRounded,
  EditRounded,
  LinkRounded,
  NotesRounded,
  ReplayRounded,
  ScheduleRounded,
  SendRounded,
  VisibilityRounded,
  WarningAmberRounded,
  HistoryRounded,
  RateReviewRounded,
  ExpandMoreRounded,
} from '@mui/icons-material';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useTenantStore,
  useTenantTimeZone,
  useTenantDateFormat,
  useTenantTimeFormat,
} from '../../../stores/tenant.store';
import { formatInstant } from '@dosfilos/shared';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import {
  assignmentApi,
  type AssignmentType,
  type PdfAnnotationDto,
  type RubricCriterionDto,
  type SubmissionDto,
  type SubmissionFileDto,
  type FormativeBreakdownDto,
} from '../../../lib/api/assignment.api';

// pdfjs is heavy — only pull it in when the student opens their corrected PDF.
const PdfViewer = lazy(() => import('../../sis/components/PdfViewer'));
import { SubmitAssignmentDialog } from '../../portal/components/SubmitAssignmentDialog';
import { QuizTakerPanel } from '../components/QuizTakerPanel';
import { SocraticDialoguePanel } from '../components/SocraticDialoguePanel';
import { useAuth } from '../../../contexts/auth.context';
import { useSectionAssignments } from '../hooks/use-portal-data';
import { PortalLoading } from '../PortalShell';
import { Markdown } from '../../../components/Markdown';
import { markGradeSeen } from '../util/seen-grades';
import { errorText } from '../../../lib/error-text';

function typeLabel(t: TFunction, type: AssignmentType): string {
  const KEY: Record<AssignmentType, string> = {
    'reading-review': 'portal.assignmentDetail.typeReadingReview',
    essay: 'portal.assignmentDetail.typeEssay',
    quiz: 'portal.assignmentDetail.typeQuiz',
    project: 'portal.assignmentDetail.typeProject',
    attendance: 'portal.assignmentDetail.typeAttendance',
    socratic: 'portal.assignmentDetail.typeSocratic',
    other: 'portal.assignmentDetail.typeOther',
  };
  return t(KEY[type]);
}

/** Flatten an assignment's instructions (plain string or TipTap JSON) to text. */
function instructionsToText(desc?: Record<string, unknown> | string): string {
  if (!desc) return '';
  if (typeof desc === 'string') return desc;
  const blocks: string[] = [];
  const collect = (node: unknown): string => {
    if (!node || typeof node !== 'object') return '';
    const n = node as Record<string, unknown>;
    let s = typeof n['text'] === 'string' ? (n['text'] as string) : '';
    if (Array.isArray(n['content'])) s += (n['content'] as unknown[]).map(collect).join('');
    return s;
  };
  const content = (desc as { content?: unknown[] }).content;
  if (Array.isArray(content)) for (const b of content) blocks.push(collect(b));
  else blocks.push(collect(desc));
  return blocks.join('\n').trim();
}

function isPdf(name: string, mime?: string): boolean {
  return mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
}

export function AssignmentDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('sis');
  const { sectionId, assignmentId } = useParams<{ sectionId: string; assignmentId: string }>();
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const tenantTz = useTenantTimeZone();
  const tenantFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();
  const { personId } = useStudentIdentity();
  const { user } = useAuth();
  const { data, refetch } = useSectionAssignments(sectionId);
  const [submission, setSubmission] = useState<SubmissionDto | null>(null);
  const [loadingSub, setLoadingSub] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [rubric, setRubric] = useState<RubricCriterionDto[]>([]);
  const [preview, setPreview] = useState<{ title: string; url: string } | null>(null);
  const [correctionsOpen, setCorrectionsOpen] = useState(false);

  const assignment = data?.items.find((a) => a.id === assignmentId);

  useEffect(() => {
    if (!assignment?.studentSubmission?.id || !currentTenantId) return;
    setLoadingSub(true);
    assignmentApi
      .get({
        tenantId: currentTenantId,
        submissionId: assignment.studentSubmission.id,
        ...(personId ? { requestingStudentId: personId } : {}),
      })
      .then(setSubmission)
      .finally(() => setLoadingSub(false));
  }, [assignment?.studentSubmission?.id, currentTenantId, personId]);

  // Load the rubric (for the per-criterion breakdown) once the work is graded.
  const hasRubricAssessment = !!submission?.grade?.rubricAssessment?.length;
  useEffect(() => {
    if (!hasRubricAssessment || !currentTenantId || !assignmentId || rubric.length > 0) return;
    assignmentApi
      .getAssignmentRubric({ tenantId: currentTenantId, assignmentId })
      .then(({ criteria }) => setRubric(criteria))
      .catch(() => {
        /* breakdown is best-effort */
      });
  }, [hasRubricAssessment, currentTenantId, assignmentId, rubric.length]);

  // Opening the graded view marks the grade as seen (clears the dashboard "Nuevo").
  useEffect(() => {
    if (submission?.grade?.finalPoints !== undefined) {
      markGradeSeen(personId, submission.id, submission.grade.finalPoints);
    }
  }, [submission, personId]);

  const rubricById = useMemo(() => {
    const m = new Map<string, RubricCriterionDto>();
    for (const c of rubric) m.set(c.id, c);
    return m;
  }, [rubric]);

  // The student's PDF + the teacher's marks on it (present once graded/returned).
  const pdfFile = submission?.files.find((f) => isPdf(f.originalFileName, f.mimeType));
  const pdfAnnotations = useMemo(
    () => (submission?.annotations ?? []).filter((a) => a.fileStoragePath === pdfFile?.storagePath),
    [submission?.annotations, pdfFile?.storagePath],
  );

  // On wide screens show the corrected PDF side-by-side with the feedback (the
  // teacher's own two-pane view). Narrower → single column + a dialog button.
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));

  // Re-render exactly when the close time passes, so the CTA flips to "closed"
  // live (isClosed is otherwise only evaluated on render → stale until refresh).
  // Declared BEFORE the early return below to keep hook order stable.
  const [, setLockTick] = useState(0);
  const lockAt = assignment?.lockAt;
  useEffect(() => {
    if (!lockAt) return;
    const ms = new Date(lockAt).getTime() - Date.now();
    if (ms <= 0 || ms > 2_147_483_647) return;
    const id = setTimeout(() => setLockTick((n) => n + 1), ms + 500);
    return () => clearTimeout(id);
  }, [lockAt]);

  if (!assignment) return <PortalLoading />;

  const isPastDue = assignment.dueAt && new Date(assignment.dueAt) < new Date();
  const isQuiz = assignment.type === 'quiz';
  const isSocratic = assignment.type === 'socratic';
  const sub = assignment.studentSubmission;
  const status = sub?.status;
  const isReturnedForRevision = status === 'returned' && !submission?.grade;
  const isGraded = status === 'graded' || (status === 'returned' && !!submission?.grade);
  const isSubmitted = status === 'submitted' || isGraded;
  const submittedLate =
    isSubmitted && sub?.submittedAt && assignment.dueAt && sub.submittedAt > assignment.dueAt;
  // Closed: lockAt passed and the student hasn't submitted — the server rejects
  // the submission, so block it in the UI with an explanation.
  const isClosed = !isSubmitted && !!assignment.lockAt && new Date(assignment.lockAt) < new Date();

  // Two-pane (PDF + feedback) when graded, wide, and there are marks to show.
  const showInlinePdf =
    isGraded && isWide && !!pdfFile && pdfAnnotations.length > 0 && !!currentTenantId;

  const handleSubmitted = () => {
    setDialogOpen(false);
    refetch();
  };

  const handleCorrect = async () => {
    if (!currentTenantId || !sub) return;
    setReopening(true);
    setReopenError(null);
    try {
      await assignmentApi.reopenSubmission({ tenantId: currentTenantId, submissionId: sub.id });
      await refetch();
      setDialogOpen(true);
    } catch (e) {
      // The attempt-reopening policy (past due / review started) is enforced
      // server-side; surface WHY so the student isn't left with a dead button.
      const reason = (e as { details?: { reason?: string } })?.details?.reason;
      setReopenError(
        reason === 'reopen.past_due'
          ? t('portal.assignmentDetail.reopenError.pastDue')
          : reason === 'reopen.review_started'
            ? t('portal.assignmentDetail.reopenError.reviewStarted')
            : reason === 'reopen.locked'
              ? t('portal.assignmentDetail.reopenError.locked')
              : t('portal.assignmentDetail.reopenError.generic'),
      );
    } finally {
      setReopening(false);
    }
  };

  const reopenErrorAlert = reopenError ? (
    <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
      {reopenError}
    </Alert>
  ) : null;

  const levelNameFor = (criterionId: string, score: number): string | null => {
    const c = rubricById.get(criterionId);
    if (!c?.levels?.length) return null;
    const idx = Math.min(c.levels.length - 1, Math.max(0, Math.round(score * c.levels.length) - 1));
    return c.levels[idx]?.name ?? null;
  };

  // The socratic dialogue takes over the screen (its own full-screen panel).
  if (dialogOpen && isSocratic && currentTenantId && user) {
    return (
      <SocraticDialoguePanel
        tenantId={currentTenantId}
        studentUid={user.uid}
        assignmentId={assignment.id}
        uiLocale={(i18n.language?.startsWith('en') ? 'en' : 'es') as 'es' | 'en'}
        onClose={() => {
          setDialogOpen(false);
          void refetch();
        }}
      />
    );
  }

  // Taking a quiz takes over the central column (not a modal): more room for a
  // long quiz and no risk of losing progress by closing a dialog.
  if (dialogOpen && isQuiz && currentTenantId && personId) {
    return (
      <Box sx={{ maxWidth: 920, mx: 'auto', width: '100%' }}>
        <Button
          size="small"
          startIcon={<ArrowBackRounded />}
          onClick={() => setDialogOpen(false)}
          sx={{ color: '#8b949e', mb: 1, ml: -0.5 }}
        >
          {t('portal.assignmentDetail.back')}
        </Button>
        <QuizTakerPanel
          tenantId={currentTenantId}
          studentId={personId}
          assignmentId={assignment.id}
          onClose={() => setDialogOpen(false)}
          onSubmitted={() => refetch()}
        />
      </Box>
    );
  }

  return (
    <Stack spacing={3} direction={{ xs: 'column', md: 'row' }} alignItems="flex-start">
      {/* Corrected PDF, left pane (wide screens) — the teacher's two-pane view. */}
      {showInlinePdf && pdfFile && currentTenantId && (
        <Box
          sx={{
            position: 'sticky',
            top: 8,
            flexBasis: '54%',
            maxWidth: '54%',
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <CorrectedPdfPanel
            tenantId={currentTenantId}
            submissionId={submission!.id}
            storagePath={pdfFile.storagePath}
            annotations={pdfAnnotations}
            height="84vh"
            {...(personId ? { studentId: personId } : {})}
          />
        </Box>
      )}

      {/* Main content — graded view drops the right rail and reads as one
          centered, hierarchical column (no duplicated score, no empty rail).
          In two-pane mode it becomes the right column (no max-width cap). */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          width: '100%',
          ...(isGraded && !showInlinePdf ? { maxWidth: 920, mx: 'auto' } : {}),
          ...(showInlinePdf ? { maxHeight: '88vh', overflowY: 'auto', pr: 0.5 } : {}),
        }}
      >
        <Button
          size="small"
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate(`/portal-v2/courses/${sectionId}/assignments`)}
          sx={{ color: '#8b949e', mb: 1, ml: -0.5 }}
        >
          {t('portal.assignmentDetail.back')}
        </Button>

        <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
          <Chip
            size="small"
            label={typeLabel(t, assignment.type)}
            sx={{
              bgcolor: 'rgba(99,102,241,0.18)',
              color: '#a5b4fc',
              fontWeight: 700,
              letterSpacing: 0.3,
              height: 22,
            }}
          />
        </Stack>
        <Typography
          variant="h5"
          fontWeight={700}
          color="#e6edf3"
          mb={1.5}
          sx={{ fontSize: { xs: 20, sm: 26 }, lineHeight: 1.25, wordBreak: 'break-word' }}
        >
          {assignment.name}
        </Typography>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={2} useFlexGap>
          {assignment.dueAt && (
            <Chip
              size="small"
              icon={<ScheduleRounded sx={{ fontSize: 14 }} />}
              label={t('portal.assignmentDetail.dueChip', {
                date: formatInstant(assignment.dueAt, tenantTz, tenantFmt, tenantTimeFmt),
              })}
              color={isPastDue && !isSubmitted ? 'warning' : 'default'}
              variant="outlined"
            />
          )}
          {assignment.lockAt && !isClosed && (
            <Chip
              size="small"
              label={t('portal.assignmentDetail.closesChip', {
                date: formatInstant(assignment.lockAt, tenantTz, tenantFmt, tenantTimeFmt),
              })}
              color="default"
              variant="outlined"
              sx={{ borderColor: '#30363d' }}
            />
          )}
          {isClosed && (
            <Chip
              size="small"
              label={t('portal.assignmentDetail.closedChip')}
              color="error"
              variant="outlined"
            />
          )}
          <Chip
            size="small"
            label={t('portal.assignmentDetail.pointsChip', { points: assignment.pointsPossible })}
            variant="outlined"
            sx={{ borderColor: '#30363d' }}
          />
          <Chip
            size="small"
            label={t('portal.assignmentDetail.weightChip', { weight: assignment.weight })}
            variant="outlined"
            sx={{ borderColor: '#30363d' }}
          />
        </Stack>

        {/* Returned for revision — the prominent "fix & resubmit" moment. */}
        {isReturnedForRevision && (
          <Card
            sx={{
              bgcolor: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.4)',
              borderRadius: 2,
              p: 2.5,
              mb: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <ReplayRounded sx={{ color: '#f59e0b' }} />
              <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
                {t('portal.assignmentDetail.revisionRequestedTitle')}
              </Typography>
            </Stack>
            {(() => {
              const lockFuture = !!assignment.lockAt && new Date(assignment.lockAt) > new Date();
              const parts: string[] = [];
              if (submission?.attempt)
                parts.push(t('portal.assignmentDetail.attemptLabel', { n: submission.attempt }));
              if (lockFuture)
                parts.push(
                  t('portal.assignmentDetail.resubmitBy', {
                    date: formatInstant(assignment.lockAt!, tenantTz, tenantFmt, tenantTimeFmt),
                  }),
                );
              return parts.length > 0 ? (
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  {parts.join(' · ')}
                </Typography>
              ) : null;
            })()}
            {submission?.returnNote && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: '#0d1117',
                  border: '1px solid #21262d',
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap' }}>
                  {submission.returnNote}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              color="warning"
              startIcon={reopening ? <CircularProgress size={16} /> : <EditRounded />}
              onClick={handleCorrect}
              disabled={reopening || !personId}
            >
              {t('portal.assignmentDetail.fixAndResubmit')}
            </Button>
            {reopenErrorAlert}
          </Card>
        )}

        {/* Graded — grade + feedback + per-criterion rubric breakdown. */}
        {isGraded && sub && (
          <Card
            sx={{
              bgcolor: 'rgba(16,185,129,0.06)',
              border: '1px solid rgba(16,185,129,0.3)',
              borderRadius: 2,
              p: 2.5,
              mb: 3,
            }}
          >
            {/* Screen-reader announcement of the grade on load. */}
            <Box
              role="status"
              aria-live="polite"
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                p: 0,
                m: -1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {t('portal.assignmentDetail.gradeAnnounce', {
                points: sub.finalPoints?.toFixed(1),
                total: assignment.pointsPossible,
              })}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
              <CheckCircleRounded aria-hidden="true" sx={{ color: '#10b981', fontSize: 26 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="#10b981"
                  sx={{ letterSpacing: 0.6 }}
                >
                  {t('portal.assignmentDetail.gradedLabel')}
                </Typography>
                {sub.submittedAt && (
                  <Typography variant="caption" color="#8b949e">
                    {t('portal.assignmentDetail.submittedOn', {
                      date: formatInstant(sub.submittedAt, tenantTz, tenantFmt, tenantTimeFmt),
                    })}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="h3" fontWeight={800} color="#10b981" sx={{ lineHeight: 1 }}>
                  {sub.finalPoints?.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="#8b949e">
                  {t('portal.assignmentDetail.outOfPoints', { points: assignment.pointsPossible })}
                </Typography>
              </Box>
            </Stack>
            {sub.latePenalty !== undefined && sub.latePenalty > 0 && (
              <Typography variant="caption" color="#f59e0b" display="block">
                {t('portal.assignmentDetail.latePenalty', {
                  percent: (sub.latePenalty * 100).toFixed(0),
                })}
              </Typography>
            )}
          </Card>
        )}

        {/* Resumen formativo estructurado — tarjeta escaneable (lo logrado /
            a mejorar / siguiente paso), sobre la prosa. Sólo cuando existe. */}
        {isGraded && submission?.grade?.formativeBreakdown && (
          <FormativeBreakdownCard breakdown={submission.grade.formativeBreakdown} />
        )}

        {/* Comentarios del profesor — readable "document" surface, collapsible. */}
        {isGraded && submission?.grade?.feedback && (
          <CollapsibleSection
            title={t('portal.assignmentDetail.teacherCommentsTitle')}
            icon={<RateReviewRounded sx={{ fontSize: 16, color: '#8b949e' }} />}
          >
            <FeedbackDoc markdown={submission.grade.feedback} />
          </CollapsibleSection>
        )}

        {/* Desglose por criterio — collapsible. */}
        {isGraded && hasRubricAssessment && (
          <CollapsibleSection title={t('portal.assignmentDetail.rubricBreakdownTitle')}>
            <Stack spacing={1}>
              {submission!.grade!.rubricAssessment!.map((entry) => {
                const level = levelNameFor(entry.criterionId, entry.score);
                const label =
                  rubricById.get(entry.criterionId)?.label ??
                  t('portal.assignmentDetail.criterionFallback');
                return (
                  <Box
                    key={entry.criterionId}
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      bgcolor: '#0d1117',
                      border: '1px solid #21262d',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" fontWeight={600} color="#e6edf3" sx={{ flex: 1 }}>
                        {label}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          level
                            ? `${level} · ${Math.round(entry.score * 100)}%`
                            : `${Math.round(entry.score * 100)}%`
                        }
                        sx={{
                          height: 20,
                          bgcolor: 'rgba(16,185,129,0.15)',
                          color: '#10b981',
                          fontWeight: 700,
                        }}
                      />
                    </Stack>
                    {entry.comment && (
                      <Typography
                        variant="caption"
                        color="#8b949e"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {entry.comment}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </CollapsibleSection>
        )}

        {/* Corrected work — the teacher's marks ON the student's PDF. The most
            useful artifact for learning, so it's surfaced as a primary action.
            Hidden when the PDF is already shown inline (two-pane). */}
        {isGraded && !showInlinePdf && pdfFile && pdfAnnotations.length > 0 && currentTenantId && (
          <Card
            sx={{
              bgcolor: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.35)',
              borderRadius: 2,
              p: 2.5,
              mb: 3,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
            >
              <RateReviewRounded sx={{ color: '#a5b4fc', fontSize: 26 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
                  {t('portal.assignmentDetail.correctedWorkTitle')}
                </Typography>
                <Typography variant="body2" color="#8b949e">
                  {pdfAnnotations.length === 1
                    ? t('portal.assignmentDetail.correctedWorkHintOne', {
                        count: pdfAnnotations.length,
                      })
                    : t('portal.assignmentDetail.correctedWorkHintOther', {
                        count: pdfAnnotations.length,
                      })}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<RateReviewRounded />}
                onClick={() => setCorrectionsOpen(true)}
                sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                {t('portal.assignmentDetail.viewMyCorrections')}
              </Button>
            </Stack>
          </Card>
        )}

        {/* Teacher messages (return cycle) — collapsible. */}
        {isGraded && submission?.revisionHistory && submission.revisionHistory.length > 0 && (
          <CollapsibleSection
            title={t('portal.assignmentDetail.teacherMessagesTitle')}
            icon={<HistoryRounded sx={{ fontSize: 16, color: '#8b949e' }} />}
          >
            <Stack spacing={1.25}>
              {[...submission.revisionHistory]
                .sort((a, b) => b.at.localeCompare(a.at))
                .map((ev, i) => (
                  <Box
                    key={`${ev.at}-${i}`}
                    sx={{ pl: 1.25, borderLeft: '3px solid', borderColor: '#f59e0b' }}
                  >
                    <Typography variant="caption" color="#8b949e">
                      {formatInstant(ev.at, tenantTz, tenantFmt, tenantTimeFmt)}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#c9d1d9' }}>
                      {ev.note || t('portal.assignmentDetail.returnedNoMessage')}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          </CollapsibleSection>
        )}

        {/* Instructions — collapsed by default once graded (feedback takes focus). */}
        <CollapsibleSection
          title={t('portal.assignmentDetail.instructionsTitle')}
          defaultOpen={!isGraded}
        >
          {(() => {
            const text = instructionsToText(assignment.description);
            return (
              <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap' }}>
                {text ||
                  t('portal.assignmentDetail.instructionsFallback', {
                    type: typeLabel(t, assignment.type).toLowerCase(),
                    points: assignment.pointsPossible,
                  })}
              </Typography>
            );
          })()}
        </CollapsibleSection>

        {/* What the student submitted — collapsible. */}
        {isSubmitted && submission && currentTenantId && (
          <CollapsibleSection
            title={t('portal.assignmentDetail.whatYouSubmittedTitle')}
            icon={<CloudDoneRounded sx={{ color: '#3b82f6', fontSize: 16 }} />}
            defaultOpen={!isGraded}
          >
            <SubmissionPreview
              submission={submission}
              tenantId={currentTenantId}
              onPreview={(title, url) => setPreview({ title, url })}
              {...(personId ? { studentId: personId } : {})}
            />
          </CollapsibleSection>
        )}
      </Box>

      {/* Sticky sidebar — only for non-graded states (status + primary action).
          The graded view is single-column; its score/messages live in the main
          content, so no rail (avoids the duplicated score + empty column). */}
      {!isGraded && (
        <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
          <Card
            sx={{
              bgcolor: '#161b22',
              border: '1px solid #21262d',
              borderRadius: 2,
              position: { md: 'sticky' },
              top: { md: 24 },
            }}
          >
            <Box sx={{ p: 2.5 }}>
              <Typography variant="overline" color="#8b949e" fontWeight={700}>
                {t('portal.assignmentDetail.mySubmission')}
              </Typography>
              {loadingSub ? (
                <Stack spacing={1.5} mt={1.5} aria-busy="true">
                  <Skeleton
                    variant="rounded"
                    width={120}
                    height={28}
                    sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                  />
                  <Skeleton variant="text" width="70%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                  <Skeleton
                    variant="rounded"
                    height={36}
                    sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                  />
                </Stack>
              ) : isReturnedForRevision ? (
                <Stack spacing={1.5} mt={1.5}>
                  <Chip
                    icon={<ReplayRounded sx={{ fontSize: 16 }} />}
                    label={t('portal.assignmentDetail.statusReturnedForRevision')}
                    color="warning"
                    sx={{ width: 'fit-content' }}
                  />
                  <Typography variant="caption" color="#8b949e">
                    {t('portal.assignmentDetail.revisionSidebarHint')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={reopening ? <CircularProgress size={16} /> : <EditRounded />}
                    onClick={handleCorrect}
                    disabled={reopening || !personId}
                  >
                    {t('portal.assignmentDetail.fixAndResubmit')}
                  </Button>
                  {reopenErrorAlert}
                </Stack>
              ) : isGraded ? (
                <Stack spacing={1.5} mt={1.5}>
                  <Chip
                    icon={<CheckCircleRounded sx={{ fontSize: 16 }} />}
                    label={t('portal.assignmentDetail.statusGraded')}
                    color="success"
                    sx={{ width: 'fit-content' }}
                  />
                  <Typography variant="h5" fontWeight={700} color="#10b981">
                    {sub?.finalPoints?.toFixed(1)} / {assignment.pointsPossible}
                  </Typography>
                  {sub?.submittedAt && (
                    <Typography variant="caption" color="#8b949e">
                      {t('portal.assignmentDetail.submittedOn', {
                        date: formatInstant(sub.submittedAt, tenantTz, tenantFmt, tenantTimeFmt),
                      })}
                    </Typography>
                  )}
                </Stack>
              ) : isSubmitted ? (
                <Stack spacing={1.5} mt={1.5}>
                  <Chip
                    icon={<CloudDoneRounded sx={{ fontSize: 16 }} />}
                    label={t('portal.assignmentDetail.statusSubmittedWaiting')}
                    color="info"
                    sx={{ width: 'fit-content' }}
                  />
                  {submittedLate && (
                    <Chip
                      icon={<WarningAmberRounded sx={{ fontSize: 16 }} />}
                      label={t('portal.assignmentDetail.statusSubmittedLate')}
                      sx={{
                        width: 'fit-content',
                        bgcolor: 'rgba(245,158,11,0.18)',
                        color: '#fbbf24',
                        '& .MuiChip-icon': { color: '#fbbf24' },
                      }}
                      size="small"
                    />
                  )}
                  {sub?.submittedAt && (
                    <Typography variant="caption" color="#8b949e">
                      {t('portal.assignmentDetail.submittedOn', {
                        date: formatInstant(sub.submittedAt, tenantTz, tenantFmt, tenantTimeFmt),
                      })}
                    </Typography>
                  )}
                  {submittedLate && (
                    <Typography variant="caption" color="#fbbf24">
                      {t('portal.assignmentDetail.latePenaltyWarning')}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1, borderColor: '#21262d' }} />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={reopening ? <CircularProgress size={16} /> : <EditRounded />}
                    onClick={handleCorrect}
                    disabled={reopening || !personId}
                  >
                    {t('portal.assignmentDetail.resubmit')}
                  </Button>
                  {reopenErrorAlert}
                </Stack>
              ) : isClosed ? (
                <Stack spacing={1.5} mt={1.5}>
                  <Alert severity="error" sx={{ py: 0.5 }}>
                    {assignment.lockAt
                      ? t('portal.assignmentDetail.closedAlertWithDate', {
                          date: formatInstant(
                            assignment.lockAt,
                            tenantTz,
                            tenantFmt,
                            tenantTimeFmt,
                          ),
                        })
                      : t('portal.assignmentDetail.closedAlert')}
                  </Alert>
                </Stack>
              ) : (
                <Stack spacing={1.5} mt={1.5}>
                  {isPastDue && (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      {t('portal.assignmentDetail.lateWarning')}
                    </Alert>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<SendRounded />}
                    onClick={() => setDialogOpen(true)}
                    disabled={isSocratic ? !user : !personId}
                  >
                    {isSocratic
                      ? t('portal.socratic.open')
                      : isQuiz
                        ? t('portal.assignmentDetail.quiz.takeQuiz')
                        : t('portal.assignmentDetail.submitAssignment')}
                  </Button>
                  {assignment.dueAt && (
                    <Typography variant="caption" color="#8b949e" textAlign="center">
                      {t('portal.assignmentDetail.dueOn', {
                        date: formatInstant(assignment.dueAt, tenantTz, tenantFmt, tenantTimeFmt),
                      })}
                    </Typography>
                  )}
                  {assignment.lockAt && (
                    <Typography variant="caption" color="#8b949e" textAlign="center">
                      {t('portal.assignmentDetail.closesOn', {
                        date: formatInstant(assignment.lockAt, tenantTz, tenantFmt, tenantTimeFmt),
                      })}
                    </Typography>
                  )}
                </Stack>
              )}
              {submission?.revisionHistory && submission.revisionHistory.length > 0 && (
                <Box mt={2} pt={2} sx={{ borderTop: '1px solid #21262d' }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
                    <HistoryRounded sx={{ fontSize: 16, color: '#8b949e' }} />
                    <Typography variant="overline" color="#8b949e" fontWeight={700}>
                      {t('portal.assignmentDetail.teacherMessagesOverline')}
                    </Typography>
                  </Stack>
                  <Stack spacing={1.25}>
                    {[...submission.revisionHistory]
                      .sort((a, b) => b.at.localeCompare(a.at))
                      .map((ev, i) => (
                        <Box
                          key={`${ev.at}-${i}`}
                          sx={{ pl: 1.25, borderLeft: '3px solid', borderColor: '#f59e0b' }}
                        >
                          <Typography variant="caption" color="#8b949e">
                            {formatInstant(ev.at, tenantTz, tenantFmt, tenantTimeFmt)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', color: '#c9d1d9' }}
                          >
                            {ev.note || t('portal.assignmentDetail.returnedNoMessage')}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                </Box>
              )}

              {!isGraded && (
                <Typography
                  variant="caption"
                  color="#6e7681"
                  sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <AutoAwesomeRounded sx={{ fontSize: 14 }} />{' '}
                  {t('portal.assignmentDetail.feedbackPromise')}
                </Typography>
              )}
            </Box>
          </Card>
        </Box>
      )}

      {dialogOpen && currentTenantId && personId && !isQuiz && (
        <SubmitAssignmentDialog
          tenantId={currentTenantId}
          assignment={assignment}
          onClose={() => setDialogOpen(false)}
          onSubmitted={handleSubmitted}
        />
      )}

      {/* Inline file preview */}
      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh', bgcolor: '#161b22' } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ flex: 1 }}
              noWrap
              color="#e6edf3"
            >
              {preview?.title}
            </Typography>
            {preview && (
              <Tooltip title={t('portal.assignmentDetail.openInNewTab')}>
                <IconButton
                  size="small"
                  onClick={() => window.open(preview.url, '_blank', 'noopener,noreferrer')}
                >
                  <DownloadRounded fontSize="small" sx={{ color: '#8b949e' }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setPreview(null)}>
              <CloseRounded fontSize="small" sx={{ color: '#8b949e' }} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {preview && (
            <iframe
              title={preview.title}
              src={preview.url}
              style={{ width: '100%', height: '74vh', border: 'none', borderRadius: 6 }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Corrected PDF with the teacher's marks (annotations). */}
      {correctionsOpen && pdfFile && currentTenantId && submission && (
        <CorrectionsDialog
          tenantId={currentTenantId}
          submissionId={submission.id}
          storagePath={pdfFile.storagePath}
          annotations={pdfAnnotations}
          {...(personId ? { studentId: personId } : {})}
          onClose={() => setCorrectionsOpen(false)}
        />
      )}
    </Stack>
  );
}

// ─── Corrected PDF (teacher annotations, read-only) ──────────────────────────

function CorrectionsDialog({
  tenantId,
  submissionId,
  storagePath,
  annotations,
  studentId,
  onClose,
}: {
  tenantId: string;
  submissionId: string;
  storagePath: string;
  annotations: PdfAnnotationDto[];
  studentId?: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      PaperProps={{ sx: { bgcolor: '#161b22' } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <RateReviewRounded sx={{ color: '#a5b4fc' }} fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
            {t('portal.assignmentDetail.correctedWorkTitle')}
          </Typography>
        </Stack>
        <Typography variant="caption" color="#8b949e">
          {t('portal.assignmentDetail.correctionsDialogHint')}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
          <CloseRounded fontSize="small" sx={{ color: '#8b949e' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <CorrectedPdfPanel
          tenantId={tenantId}
          submissionId={submissionId}
          storagePath={storagePath}
          annotations={annotations}
          height="74vh"
          {...(studentId ? { studentId } : {})}
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── Submission preview ──────────────────────────────────────────────────────

function SubmissionPreview({
  submission,
  tenantId,
  studentId,
  onPreview,
}: {
  submission: SubmissionDto;
  tenantId: string;
  studentId?: string;
  onPreview: (title: string, url: string) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Stack spacing={2}>
      {submission.files.length > 0 && (
        <Stack spacing={1}>
          {submission.files.map((file) => (
            <FileRow
              key={file.storagePath}
              file={file}
              tenantId={tenantId}
              submissionId={submission.id}
              onPreview={onPreview}
              {...(studentId ? { studentId } : {})}
            />
          ))}
        </Stack>
      )}

      {submission.url && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 1.25,
            borderRadius: 1,
            border: '1px solid #21262d',
            bgcolor: '#0d1117',
          }}
        >
          <LinkRounded sx={{ color: '#a5b4fc', fontSize: 20 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="#8b949e" display="block">
              {t('portal.assignmentDetail.linkLabel')}
            </Typography>
            <Typography
              variant="body2"
              component="a"
              href={submission.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#c9d1d9',
                textDecoration: 'none',
                wordBreak: 'break-all',
                '&:hover': { color: '#a5b4fc', textDecoration: 'underline' },
              }}
            >
              {submission.url}
            </Typography>
          </Box>
        </Box>
      )}

      {submission.textBody && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <NotesRounded sx={{ color: '#8b949e', fontSize: 16 }} />
            <Typography variant="caption" color="#8b949e" fontWeight={700}>
              {t('portal.assignmentDetail.submittedTextLabel')}
            </Typography>
          </Stack>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: '1px solid #21262d',
              bgcolor: '#0d1117',
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            <Typography
              variant="body2"
              color="#c9d1d9"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
            >
              {submission.textBody}
            </Typography>
          </Box>
        </Box>
      )}

      {submission.files.length === 0 && !submission.url && !submission.textBody && (
        <Typography variant="caption" color="#8b949e">
          {t('portal.assignmentDetail.noContentSubmitted')}
        </Typography>
      )}
    </Stack>
  );
}

function FileRow({
  file,
  tenantId,
  submissionId,
  studentId,
  onPreview,
}: {
  file: SubmissionFileDto;
  tenantId: string;
  submissionId: string;
  studentId?: string;
  onPreview: (title: string, url: string) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [busy, setBusy] = useState<false | 'view' | 'download'>(false);
  const [err, setErr] = useState<string | null>(null);
  const viewable = isPdf(file.originalFileName, file.mimeType);

  const getUrl = async (): Promise<string> => {
    const { url } = await assignmentApi.getFileUrl({
      tenantId,
      submissionId,
      storagePath: file.storagePath,
      ...(studentId ? { requestingStudentId: studentId } : {}),
    });
    return url;
  };

  const handleView = async () => {
    setErr(null);
    setBusy('view');
    try {
      onPreview(file.originalFileName, await getUrl());
    } catch (e) {
      setErr(errorText(e, t('portal.assignmentDetail.fileOpenError')));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setErr(null);
    setBusy('download');
    try {
      window.open(await getUrl(), '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(errorText(e, t('portal.assignmentDetail.fileOpenError')));
    } finally {
      setBusy(false);
    }
  };

  const sizeLabel = file.sizeBytes ? formatBytes(file.sizeBytes) : null;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          p: 1.25,
          borderRadius: 1,
          border: '1px solid #21262d',
          bgcolor: '#0d1117',
        }}
      >
        <AttachFileRounded sx={{ color: '#a5b4fc', fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="#e6edf3" noWrap>
            {file.originalFileName}
          </Typography>
          <Typography variant="caption" color="#8b949e">
            {file.mimeType ?? t('portal.assignmentDetail.fileGeneric')}
            {sizeLabel ? ` · ${sizeLabel}` : ''}
          </Typography>
        </Box>
        {viewable && (
          <Button
            size="small"
            variant="contained"
            startIcon={busy === 'view' ? <CircularProgress size={14} /> : <VisibilityRounded />}
            onClick={handleView}
            disabled={!!busy}
          >
            {t('portal.assignmentDetail.view')}
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={busy === 'download' ? <CircularProgress size={14} /> : <DownloadRounded />}
          onClick={handleDownload}
          disabled={!!busy}
        >
          {t('portal.assignmentDetail.download')}
        </Button>
      </Box>
      {err && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {err}
        </Typography>
      )}
    </Box>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Collapsible section (student review) ────────────────────────────────────

function CollapsibleSection({
  title,
  icon,
  defaultOpen = true,
  accent,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  accent?: string;
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setOpen((o) => !o)}
        sx={{ px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none' }}
      >
        {icon}
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ flex: 1, letterSpacing: 0.4, color: accent ?? '#8b949e' }}
        >
          {title}
        </Typography>
        <ExpandMoreRounded
          fontSize="small"
          sx={{
            color: '#8b949e',
            transform: open ? 'none' : 'rotate(-90deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 2 }}>{children}</Box>
      </Collapse>
    </Card>
  );
}

// ─── Feedback as a readable "document" surface ───────────────────────────────
// Dark reading page tuned to the portal-v2 shell, with comfortable measure and
// spacing so the teacher's prose (which uses bold lead-ins like "Forma y
// estilo") reads like a graded essay instead of a cramped block.

function FeedbackDoc({ markdown }: { markdown: string }): JSX.Element {
  return (
    <Box
      sx={{
        bgcolor: '#0d1117',
        color: '#c9d1d9',
        borderRadius: 1.5,
        border: '1px solid #21262d',
        px: { xs: 2.5, sm: 4 },
        py: { xs: 2.5, sm: 3.5 },
        maxWidth: '70ch',
        mx: 'auto',
        // Reading typography + turn the AI's bold lead-ins into spaced subheads.
        '& p': { lineHeight: 1.75, margin: '0 0 1em', fontSize: 15 },
        '& p:last-of-type': { marginBottom: 0 },
        '& strong': { color: '#e6edf3', fontWeight: 700 },
        // A paragraph that STARTS with a bold run (the AI's section lead-in)
        // gets extra top space + the bold acts as a heading.
        '& p > strong:first-of-type': {
          display: 'inline-block',
          marginBottom: '0.15em',
          fontSize: 16,
        },
        '& h1,& h2,& h3': { color: '#e6edf3', marginTop: '1.2em', marginBottom: '0.4em' },
        '& ul,& ol': { paddingLeft: '1.3em', lineHeight: 1.75 },
        '& li': { marginBottom: '0.35em' },
        '& a': { color: '#a5b4fc' },
        '& blockquote': {
          borderLeft: '3px solid #30363d',
          margin: '0 0 1em',
          paddingLeft: '1em',
          color: '#8b949e',
        },
      }}
    >
      <Markdown>{markdown}</Markdown>
    </Box>
  );
}

// ─── Formative breakdown card ────────────────────────────────────────────────
// The three-part scannable summary (lo logrado / a mejorar / siguiente paso)
// the teacher approved. Rows with an empty field are hidden.

function FormativeBreakdownCard({ breakdown }: { breakdown: FormativeBreakdownDto }): JSX.Element {
  const { t } = useTranslation('sis');
  const rows = [
    {
      key: 'achieved' as const,
      label: t('portal.assignmentDetail.breakdownAchieved'),
      value: breakdown.achieved,
      icon: <CheckCircleRounded sx={{ fontSize: 18, color: '#10b981' }} />,
      accent: '#10b981',
    },
    {
      key: 'missing' as const,
      label: t('portal.assignmentDetail.breakdownMissing'),
      value: breakdown.missing,
      icon: <WarningAmberRounded sx={{ fontSize: 18, color: '#f59e0b' }} />,
      accent: '#f59e0b',
    },
    {
      key: 'nextStep' as const,
      label: t('portal.assignmentDetail.breakdownNextStep'),
      value: breakdown.nextStep,
      icon: <ArrowForwardRounded sx={{ fontSize: 18, color: '#a5b4fc' }} />,
      accent: '#a5b4fc',
    },
  ].filter((r) => r.value.trim().length > 0);

  if (rows.length === 0) return <></>;

  return (
    <Card
      sx={{
        bgcolor: '#161b22',
        border: '1px solid #21262d',
        borderRadius: 2,
        p: { xs: 1.75, sm: 2.25 },
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" sx={{ color: '#e6edf3', fontWeight: 700 }}>
          {t('portal.assignmentDetail.breakdownTitle')}
        </Typography>
        {rows.map((r) => (
          <Box
            key={r.key}
            sx={{
              display: 'flex',
              gap: 1.25,
              p: 1.25,
              borderRadius: 1.5,
              bgcolor: '#0d1117',
              border: '1px solid #21262d',
              borderLeft: `3px solid ${r.accent}`,
            }}
          >
            <Box sx={{ mt: '2px' }}>{r.icon}</Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: r.accent,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  display: 'block',
                }}
              >
                {r.label}
              </Typography>
              <Typography variant="body2" sx={{ color: '#c9d1d9', lineHeight: 1.6, mt: 0.25 }}>
                {r.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Card>
  );
}

// ─── Inline corrected-PDF panel (fetches the signed URL, renders read-only) ──

function CorrectedPdfPanel({
  tenantId,
  submissionId,
  storagePath,
  annotations,
  studentId,
  height = '82vh',
}: {
  tenantId: string;
  submissionId: string;
  storagePath: string;
  annotations: PdfAnnotationDto[];
  studentId?: string;
  height?: string | number;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let active = true;
    assignmentApi
      .getFileUrl({
        tenantId,
        submissionId,
        storagePath,
        ...(studentId ? { requestingStudentId: studentId } : {}),
      })
      .then(({ url }) => active && setUrl(url))
      .catch(() => active && setErr(true));
    return () => {
      active = false;
    };
  }, [tenantId, submissionId, storagePath, studentId]);

  if (err) return <Alert severity="error">{t('portal.assignmentDetail.pdfLoadError')}</Alert>;
  if (!url)
    return (
      <Box textAlign="center" py={6}>
        <CircularProgress size={22} />
      </Box>
    );
  return (
    <Box sx={{ '& > div': { height } }}>
      <Suspense
        fallback={
          <Box textAlign="center" py={6}>
            <CircularProgress size={20} />
          </Box>
        }
      >
        <PdfViewer url={url} mode="view" annotations={annotations} />
      </Suspense>
    </Box>
  );
}
